// The System states (spec C7; the "Sign in and system states" board, states 14 to 17), shown inside
// the app shell: No access, Page not found, Server error with its reference, and the Offline bar.
// Markup from the board: sysw, syscard with the cic icon, the title, one muted line and one button.
import { useState, type ReactNode } from 'react';
import {
  isRouteErrorResponse,
  Link,
  Navigate,
  useLocation,
  useRevalidator,
  useRouteError,
} from 'react-router';
import { ApiError } from '../auth/api';
import { signInPath } from '../auth/next';
import { ErrorLine } from '../auth/SignInLayout';
import { useUser } from '../auth/user';
import { AlertCircleIcon, HelpCircleIcon, LockIcon, WifiOffIcon } from './icons';
import { checkOnline, useOnline } from './online';
import { backTo, systemCopy as c } from './system-lines';
import { Toast } from './Toast';

function SystemCard({
  id,
  icon,
  title,
  line,
  children,
}: {
  id: string;
  icon: ReactNode;
  title: string;
  line: string;
  children: ReactNode;
}) {
  return (
    <div className="sysw">
      <div className="syscard" role="alert" aria-labelledby={id}>
        <span className="cic">{icon}</span>
        <h2 id={id} className="t16 w6 ink m0">
          {title}
        </h2>
        <p className="m0 t13 muted lh">{line}</p>
        {children}
      </div>
    </div>
  );
}

function BackButton() {
  const back = backTo(useUser().role);
  return (
    <Link className="btn btn-primary" to={back.to}>
      {back.label}
    </Link>
  );
}

export function NotFound() {
  return (
    <SystemCard id="nf-t" icon={<HelpCircleIcon />} title={c.notFoundTitle} line={c.notFoundLine}>
      <BackButton />
    </SystemCard>
  );
}

/** A renewal that is someone else's (board state 14). It never shows the insured or who has it. */
export function NoAccess() {
  return (
    <SystemCard id="na-t" icon={<LockIcon />} title={c.noAccessTitle} line={c.noAccessRecord}>
      <BackButton />
    </SystemCard>
  );
}

/** Server error: Try again reloads the page's data; a second failure says so under the button. */
export function ServerError({ errorRef }: { errorRef: string | null }) {
  const revalidator = useRevalidator();
  const [tried, setTried] = useState(false);
  const retrying = revalidator.state === 'loading';
  return (
    <SystemCard
      id="se-t"
      icon={<AlertCircleIcon />}
      title={c.serverTitle}
      line={c.serverLine(errorRef)}
    >
      <button
        type="button"
        className="btn btn-primary"
        disabled={retrying}
        onClick={() => {
          setTried(true);
          revalidator.revalidate();
        }}
      >
        {retrying && (
          <span
            className="spin"
            style={{ borderColor: 'transparent', borderTopColor: 'var(--surface)' }}
          />
        )}
        {retrying ? c.tryingAgain : c.tryAgain}
      </button>
      {tried && !retrying && <ErrorLine text={c.stillFailing} center />}
    </SystemCard>
  );
}

/** What went wrong while loading a page inside the shell, as one of the system cards. */
export function RouteError() {
  const error = useRouteError();
  const { pathname, search } = useLocation();
  if (isRouteErrorResponse(error) && error.status === 404) return <NotFound />;
  if (error instanceof ApiError) {
    // The session ended while the page loaded: sign in again and come back here.
    if (error.status === 401) {
      return (
        <Navigate to={signInPath(pathname + search, error.code === 'session_expired')} replace />
      );
    }
    // A renewal says No access, as on the board. Any other page the role may not open looks like
    // it is not there, so the card never tells someone that a page exists.
    if (error.status === 403) return pathname.startsWith('/records/') ? <NoAccess /> : <NotFound />;
    if (error.status === 404) return <NotFound />;
    return <ServerError errorRef={error.ref} />;
  }
  return <ServerError errorRef={null} />;
}

/** The bar at the top of the page while offline (board state 17). */
export function OfflineBar() {
  const online = useOnline();
  const [checking, setChecking] = useState(false);
  const [toast, setToast] = useState<number | null>(null);
  if (online) return null;
  return (
    <>
      <div className="offbar" role="alert">
        <WifiOffIcon className="ico s14" />
        <span>{c.offline}</span>
        <button
          type="button"
          className="btn btn-sm"
          disabled={checking}
          onClick={async () => {
            setChecking(true);
            const ok = await checkOnline();
            setChecking(false);
            if (!ok) setToast(Date.now());
          }}
        >
          {checking ? c.checking : c.tryAgain}
        </button>
      </div>
      {toast && <Toast key={toast} text={c.stillOffline} />}
    </>
  );
}
